import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { humanReadableDate } from "../../../helper/dateTimeHelper";
import { toProperCase } from "../../../helper/company_helper";

interface Props {
  filteredData: any[];
  handleAssignedAssetEdit: any;
  setDeleteRecord: any;
  setSelectedHistoryAsset: any;
  setSelectedLostAsset: any;
}

const AssignedAssetGrid: React.FC<Props> = ({
  filteredData,
  handleAssignedAssetEdit,
  setDeleteRecord,
  setSelectedHistoryAsset,
  setSelectedLostAsset,
}) => {
  // Pagination state
  const defaultPageSize = 8;
  const defaultPageNo = 1;
  const [currentPage, setCurrentPage] = useState(defaultPageNo);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Calculate pagination
  const totalItems = filteredData?.length || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  // Get current page data
  const currentData = useMemo(() => {
    return filteredData?.slice(startIndex, endIndex) || [];
  }, [filteredData, startIndex, endIndex]);

  // Reset to first page when filtered data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filteredData]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Generate page numbers with Ant Design logic
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  const handleViewHistory = (asset: any) => {
    setSelectedHistoryAsset(asset);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { class: string; icon: string }> = {
      Active: { class: "border-success", icon: "ti-check-circle" },
      Inactive: { class: "border-danger", icon: "ti-circle-x" },
      Pending: { class: "border-warning", icon: "ti-clock" },
      Lost: { class: "border-warning", icon: "ti-alert-triangle" },
    };
    return (
      statusConfig[status] || {
        class: "border-secondary",
        icon: "ti-help-circle",
      }
    );
  };

  const getAssignmentTypeColor = (type: string) => {
    const typeConfig: Record<string, string> = {
      user: "bg-primary-subtle text-primary-emphasis",
      department: "bg-info-subtle text-info-emphasis",
      location: "bg-warning-subtle text-warning-emphasis",
    };
    return (
      typeConfig[type?.toLowerCase()] ||
      "bg-secondary-subtle text-secondary-emphasis"
    );
  };

  const getAssignmentIcon = (type: string) => {
    const iconConfig: Record<string, string> = {
      user: "ti-user",
      department: "ti-building",
      location: "ti-map-pin",
    };
    return iconConfig[type?.toLowerCase()] || "ti-help-circle";
  };

  return (
    <>
      {/* Asset Grid */}
      <div className="row mt-3">
        {currentData &&
          currentData.map((record, index) => {
            const statusConfig = getStatusBadge(record?.status);
            const assignmentTypeColor = getAssignmentTypeColor(
              record?.assigned_to_type
            );
            const assignmentIcon = getAssignmentIcon(record?.assigned_to_type);

            return (
              <div
                key={record.id || index}
                className="col-xl-3 col-lg-4 col-md-6 col-sm-12">
                <div className="card asset-card shadow-md border-1">
                  {/* Asset Image & Status */}
                  <div className="text-center p-2">
                    {/* Status line */}
                    {/* <div className={`${statusConfig.class} border border-2 mb-3`}></div> */}

                    <div className="asset-image-container mb-1 position-relative mt-2">
                      <Link to="#" className="d-inline-block position-relative">
                        <div className="avatar avatar-xxl rounded-2 bg-light">
                          <ImageWithBasePath
                            src={
                              !record?.ProductDetails?.image?.url ||
                              record?.ProductDetails?.image?.url === "null"
                                ? "assets/img/products/product-icon.png"
                                : record?.ProductDetails?.image?.url
                            }
                            alt={record?.product_name}
                            className="w-100 h-100 object-fit-cover rounded-2"
                          />
                        </div>
                      </Link>

                      {/* Assignment Type Badge */}
                      <span
                        className={`badge ${assignmentTypeColor} position-absolute top-0 end-0 mt-1 me-0`}>
                        <i className={`ti ${assignmentIcon} me-1`}></i>
                        {toProperCase(record?.assigned_to_type || "Unknown")}
                      </span>

                      {/* Status  Dot */}
                      <span
                        className={`avatar-xl avatar-rounded online border p-1 ${
                          record?.status?.toLowerCase() === "active"
                            ? "bg-success border-success"
                            : record?.status?.toLowerCase() === "lost"
                            ? "bg-warning border-warning"
                            : "bg-danger border-danger"
                        } rounded-circle position-absolute top-0 start-0 mt-0 ms-0`}></span>
                    </div>

                    {/* Product Details */}
                    <div className="asset-details">
                      <Link to="#" className="text-decoration-none">
                        <h6
                          className="card-title mb-1 text-dark fw-semibold"
                          title={record?.product_name}>
                          {record?.product_name?.length > 25
                            ? `${record?.product_name.substring(0, 25)}...`
                            : record?.product_name || "Unknown Product"}
                        </h6>
                      </Link>

                      <div className="text-muted small mb-3">
                        <span className="badge bg-secondary-subtle text-secondary-emphasis me-1">
                          {record?.ProductDetails?.model || "N/A"}
                        </span>
                      </div>

                      {/* Assignment Information */}
                      <div className="assignment-info rounded-2 p-0 mb-0">
                        <div className="row g-2 text-start">
                          <div className="col-12">
                            <div className="d-flex align-items-start justify-content-between">
                              <span className="text-muted small">
                                <i className={`ti ti-barcode me-1`}></i>Serial
                                No.
                              </span>
                              <div className="text-end">
                                <div
                                  className="fw-medium small text-truncate"
                                  style={{ maxWidth: "120px" }}
                                  title={record?.assigned_to_name}>
                                  {record?.serial_no?.toUpperCase() || "N/A"}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="d-flex align-items-start justify-content-between">
                              <span className="text-mutes small">
                                <i className={`ti ${assignmentIcon} me-1`}></i>
                                Assigned To
                              </span>
                              <div className="text-end">
                                <div
                                  className="fw-medium small text-truncate"
                                  style={{ maxWidth: "120px" }}
                                  title={record?.assigned_to_name}>
                                  {toProperCase(
                                    record?.assigned_to_name || "N/A"
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="d-flex align-items-start justify-content-between">
                              <span className="text-muted small">
                                <i className="ti ti-user-check me-1"></i>
                                Assigned By
                              </span>
                              <div className="text-end">
                                <div
                                  className="fw-medium small text-truncate"
                                  style={{ maxWidth: "120px" }}
                                  title={record?.assigned_by_name}>
                                  {toProperCase(
                                    record?.assigned_by_name || "N/A"
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="card-footer bg-light border-0 p-2 ">
                    <div className="row g-2 mb-2">
                      {/* Assignment Date */}
                      <div className="col-6">
                        <div className="text-center">
                          <div className="text-muted small mb-1">
                            <i className="ti ti-calendar-event text-success me-1"></i>
                            Assigned
                          </div>
                          <span className="badge bg-success-subtle text-success-emphasis border-0 small">
                            {record?.assigned_date &&
                            record?.assigned_date !== ""
                              ? humanReadableDate(record?.assigned_date)
                              : "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="col-6">
                        <div className="text-center">
                          <div className="text-muted small mb-1">Status</div>
                          <span
                            className={`badge ${
                              record?.status === "Active"
                                ? "bg-success-subtle text-success-emphasis"
                                : record?.status === "Inactive"
                                ? "bg-danger-subtle text-danger-emphasis"
                                : "bg-warning-subtle text-warning-emphasis"
                            } border-0 small`}
                            title={
                              record?.status === "Lost"
                                ? record?.lost_remark || ""
                                : ""
                            }>
                            {record?.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-sm btn-outline-info flex-fill"
                        title="Show Details"
                        data-bs-toggle="modal"
                        data-bs-target="#asset-history-modal"
                        onClick={() => handleViewHistory(record)}>
                        <i className="ti ti-eye me-1"></i>View
                      </button>
                      {record?.status !== "Lost" ? (
                        <button
                          className="btn btn-sm btn-outline-primary flex-fill"
                          onClick={() => handleAssignedAssetEdit(record)}
                          data-bs-toggle="modal"
                          data-bs-target="#edit_asset_modal"
                          title="Edit Assignment">
                          <i className="ti ti-edit me-1"></i>Edit
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-success flex-fill"
                          onClick={() => setSelectedLostAsset(record)}
                          data-bs-toggle="modal"
                          data-bs-target="#mark_asset_lost_modal"
                          title="Recover Asset">
                          <i className="ti ti-refresh-dot me-1"></i>Recover
                        </button>
                      )}
                      {record?.status !== "Lost" && (
                        <button
                          className="btn btn-sm btn-outline-warning flex-fill"
                          onClick={() => setSelectedLostAsset(record)}
                          data-bs-toggle="modal"
                          data-bs-target="#mark_asset_lost_modal"
                          title="Mark as Lost">
                          <i className="ti ti-alert-triangle me-1"></i>Lost
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger flex-fill"
                        data-bs-toggle="modal"
                        data-inert={true}
                        data-bs-target="#delete_assset_modal"
                        onClick={() => setDeleteRecord(record)}
                        title="Delete Assignment">
                        <i className="ti ti-trash me-1"></i>Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Ant Design Style Pagination */}
      {filteredData && (
        <div className="d-flex justify-content-center align-items-center mt-2 px-2 gap-3">
          {/* Left side - Total info */}
          <div className="text-dark small">
            Showing {(currentPage - 1) * pageSize + 1}-
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems} items
          </div>

          {/* Center - Pagination Controls */}
          <div className="d-flex align-items-center gap-3">
            {totalPages > 1 && (
              <nav className="me-3">
                <ul className="pagination pagination-sm mb-0 antd-pagination">
                  {/* Previous Button */}
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}>
                    <button
                      className="page-link antd-pagination-prev"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      title="Previous Page">
                      <i className="ti ti-chevron-left"></i>
                    </button>
                  </li>

                  {/* First page + ellipsis */}
                  {currentPage > 3 && (
                    <>
                      <li className="page-item">
                        <button
                          className="page-link antd-pagination-item"
                          onClick={() => handlePageChange(1)}>
                          1
                        </button>
                      </li>
                      {currentPage > 4 && (
                        <li className="page-item disabled">
                          <span className="page-link antd-pagination-ellipsis">
                            •••
                          </span>
                        </li>
                      )}
                    </>
                  )}

                  {/* Page Numbers */}
                  {getPageNumbers().map((pageNum) => (
                    <li
                      key={pageNum}
                      className={`page-item ${
                        currentPage === pageNum ? "active" : ""
                      }`}>
                      <button
                        className={`page-link ${
                          currentPage === pageNum
                            ? "antd-pagination-item-active"
                            : "antd-pagination-item"
                        }`}
                        onClick={() => handlePageChange(pageNum)}>
                        {pageNum}
                      </button>
                    </li>
                  ))}

                  {/* Last page + ellipsis */}
                  {currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && (
                        <li className="page-item disabled">
                          <span className="page-link antd-pagination-ellipsis">
                            •••
                          </span>
                        </li>
                      )}
                      <li className="page-item">
                        <button
                          className="page-link antd-pagination-item"
                          onClick={() => handlePageChange(totalPages)}>
                          {totalPages}
                        </button>
                      </li>
                    </>
                  )}

                  {/* Next Button */}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}>
                    <button
                      className="page-link antd-pagination-next"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      title="Next Page">
                      <i className="ti ti-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!filteredData || filteredData.length === 0) && (
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <div className="mb-4">
                <i className="ti ti-user-x display-1 text-muted"></i>
              </div>
              <h5 className="text-muted">No Assigned Assets Found</h5>
              <p className="text-muted">
                There are no assigned assets to display at the moment.
              </p>
              <button
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#assign_asset">
                <i className="ti ti-user-plus me-2"></i>Assign First Asset
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssignedAssetGrid;
